import apiV2 from "api-v2";
import type {
	AssignmentAppliedForUser,
	AssignmentApplyResponse,
	AssignmentPlanForUser,
	AssignmentPreviewRequest,
	AssignmentPreviewResponse,
	AssignRolePayload,
	FlagTogglePayload,
	RemoveAssignmentsPayload,
	Role,
	ScopeExclusion,
	ScopeGrant,
	ScopeGrantInput,
	ScopeOption,
	ScopeOptionPage,
	ScopeType,
	UserAssignment,
	UserPage,
} from "../types/RolesPermissions.type";

// Frozen RBAC surface (TASK-011 §3) lives under /api/v2/rbac/*.
const RBAC = "/rbac";

const unwrap = <T>(responseData: unknown): T => {
	if (responseData && typeof responseData === "object" && "data" in responseData) {
		return (responseData as Record<string, unknown>).data as T;
	}
	return responseData as T;
};

// Raw paginated envelope shape shared by /rbac/scopes and /rbac/users (Laravel's native
// paginator, matching AuditLogResponse/AccessContactPage's own convention).
interface RawPage<T> {
	data: T[];
	meta: {
		current_page: number;
		per_page: number;
		total: number;
		last_page: number;
		truncated?: boolean;
	};
}

export const getUserAssignments = async (): Promise<UserAssignment[]> => {
	const response = await apiV2.get(`${RBAC}/users`);
	return unwrap<UserAssignment[]>(response.data) ?? [];
};

// CONTRACT §7 (C2) — the bulk user-picker's paginated call (25/page). The unpaginated
// `getUserAssignments()` above is UNCHANGED and stays the only call `useUserAssignments`
// makes — per CONTRACT §7, omitting page/per_page still returns the full unpaginated array.
export const getUserAssignmentsPage = async (
	params: { page?: number; perPage?: number; q?: string } = {}
): Promise<UserPage> => {
	const response = await apiV2.get<RawPage<UserAssignment>>(`${RBAC}/users`, {
		params: {
			...(params.page ? { page: params.page } : {}),
			...(params.perPage ? { per_page: params.perPage } : {}),
			// CONTRACT §7.1 — case-insensitive name/email search, applied server-side before
			// pagination. Omitted entirely when empty so a cleared search box returns to the
			// unfiltered request rather than sending `q=`.
			...(params.q ? { q: params.q } : {}),
		},
	});

	const body = response.data;
	return {
		data: body?.data ?? [],
		currentPage: body?.meta?.current_page ?? 1,
		perPage: body?.meta?.per_page ?? 25,
		total: body?.meta?.total ?? 0,
		lastPage: body?.meta?.last_page ?? 1,
	};
};

// CONTRACT §5 (G4) — `assignableToUserIds` filters the dropdown server-side to roles NOT
// already held company-wide by every given user (union rule, R3). Omitted entirely = full
// unfiltered list (back-compat for RolesList.tsx via rbac-api.ts's OWN separate getRoles,
// which this does not touch). PHP-style bracket array, matching useUsersQuery's `status[]`
// convention — there is no custom paramsSerializer on this axios instance.
export interface GetRolesOptions {
	/**
	 * CONTRACT §5.1 ADDENDUM (2026-09-24, author bug 3.2) — `exclude_event_scoped=1`, optional,
	 * default off. ONLY the direct-add role picker (`AccessModeSelector.tsx`) sends this: the
	 * Role Management admin page's OWN separate `getRoles()` (rbac-api.ts) must keep seeing every
	 * role, event-scoped included, to list/edit its matrix — a blanket server-side exclusion
	 * would have broken that surface, which is why this is an opt-in param, not a default.
	 * Deliberate defence in depth alongside the client-side `is_event_scoped !== true` filter in
	 * `AccessModeSelector.tsx` — §5.1 records that either layer alone is sufficient and neither
	 * may be removed on the grounds that the other exists.
	 */
	excludeEventScoped?: boolean;
}

export const getRoles = async (
	assignableToUserIds?: number[],
	options?: GetRolesOptions
): Promise<Role[]> => {
	const params = {
		...(assignableToUserIds && assignableToUserIds.length > 0
			? { "assignable_to_user_ids[]": assignableToUserIds }
			: {}),
		...(options?.excludeEventScoped ? { exclude_event_scoped: 1 } : {}),
	};
	const response = await apiV2.get(`${RBAC}/roles`, {
		params: Object.keys(params).length > 0 ? params : undefined,
	});
	return unwrap<Role[]>(response.data) ?? [];
};

export const assignRole = async (
	userId: number,
	payload: AssignRolePayload
): Promise<UserAssignment> => {
	const response = await apiV2.post(`${RBAC}/users/${userId}/roles`, payload);
	return unwrap<UserAssignment>(response.data);
};

export const removeRole = async (userId: number, assignmentId: number): Promise<void> => {
	await apiV2.delete(`${RBAC}/users/${userId}/roles/${assignmentId}`);
};

// CONTRACT §6 (G5) — table-view checkbox-delete, all-or-nothing on the server.
export const removeRoles = async (
	userId: number,
	assignmentIds: RemoveAssignmentsPayload["assignment_ids"]
): Promise<void> => {
	await apiV2.delete(`${RBAC}/users/${userId}/roles`, {
		data: { assignment_ids: assignmentIds } as RemoveAssignmentsPayload,
	});
};

// CONTRACT §3 (G2) — the `type:id` identity string format `exclude[]` uses. The retired
// bulk-assign wizard this replaces defined its own `scopeKey()` doing the same thing, but
// that file (and the whole wizard) is retired by D10 — this feature's permanent `api/`
// layer is the canonical home going forward; D7/D8 should import THIS rather than
// re-defining a third copy.
export const scopeKey = (type: ScopeType, id: number | null): string => `${type}:${id ?? "null"}`;

// GET /rbac/scopes — CONTRACT §3. One `type` (project_type|project|rfx) at a time, 10/page,
// server-side excluding + already-granted. Identity + parent are server-derived; the FE
// passes them through verbatim and never re-derives containment client-side.
export interface ScopeOptionsQuery {
	type?: ScopeType;
	q?: string;
	page?: number;
	perPage?: number;
	// The FULL cross-type in-progress selection (§3.2) — not scoped to the list being fetched.
	exclude?: ScopeExclusion[];
	// Single-target ("already_granted") mode only — §3.5. Omit both in multi-user mode.
	userId?: number;
	roleId?: number;
}

export const getScopeOptions = async (query: ScopeOptionsQuery = {}): Promise<ScopeOptionPage> => {
	const response = await apiV2.get<RawPage<ScopeOption>>(`${RBAC}/scopes`, {
		params: {
			...(query.type ? { type: query.type } : {}),
			...(query.q ? { q: query.q } : {}),
			...(query.page ? { page: query.page } : {}),
			...(query.perPage ? { per_page: query.perPage } : {}),
			...(query.exclude && query.exclude.length > 0
				? { "exclude[]": query.exclude.map((entry) => scopeKey(entry.scope_type, entry.scope_id)) }
				: {}),
			...(query.userId ? { user_id: query.userId } : {}),
			...(query.roleId ? { role_id: query.roleId } : {}),
		},
	});

	const body = response.data;
	return {
		data: body?.data ?? [],
		currentPage: body?.meta?.current_page ?? 1,
		perPage: body?.meta?.per_page ?? 10,
		total: body?.meta?.total ?? 0,
		lastPage: body?.meta?.last_page ?? 1,
		truncated: body?.meta?.truncated ?? false,
	};
};

// CONTRACT §11 always specifies a `{data: [...]}` envelope — it does not document an
// empty-envelope case. `unwrap`'s generic fallback ("tolerate an already-unwrapped bare
// body") returns the raw object verbatim when there is no "data" key, which is correct for
// an already-unwrapped ARRAY body but wrong here: a malformed/empty `{}` body must still
// resolve to an empty array the UI can map over, never a bare object.
const unwrapToArray = <T>(responseData: unknown): T[] => {
	const inner = unwrap<T[]>(responseData);
	return Array.isArray(inner) ? inner : [];
};

// CONTRACT §11 — the Add-flow's single write path for 1..N users. `preview` is read-only;
// `apply` re-sends the SAME request body (never the preview response echoed back) and
// executes exactly the freshly re-derived plan. Both share `AssignmentPreviewRequest`.
export const previewAssignment = async (
	payload: AssignmentPreviewRequest
): Promise<AssignmentPreviewResponse> => {
	const response = await apiV2.post(`${RBAC}/users/roles/preview`, payload);
	const body = response.data as { actionable?: unknown } | undefined;
	return {
		data: unwrapToArray<AssignmentPlanForUser>(response.data),
		// CONTRACT §11.3a — top-level, preview-only. Defensive default of `true` (fail OPEN, not
		// closed) if a malformed/older response ever omits it: the field only ever NARROWS when
		// to gate Confirm, so defaulting closed would wrongly block a real, actionable plan on a
		// contract violation the client cannot otherwise detect — the confirm dialog's own
		// per-user to_create/to_remove/blocked rendering is still the source of truth either way.
		actionable: typeof body?.actionable === "boolean" ? body.actionable : true,
	};
};

export const applyAssignment = async (
	payload: AssignmentPreviewRequest
): Promise<AssignmentApplyResponse> => {
	const response = await apiV2.post(`${RBAC}/users/roles/apply`, payload);
	return { data: unwrapToArray<AssignmentAppliedForUser>(response.data) };
};

// C12: idempotent SET — the caller sends the full desired end-state; the server reconciles/soft-revokes.
export const setScopeGrants = async (
	userId: number,
	grants: ScopeGrantInput[]
): Promise<ScopeGrant[]> => {
	const response = await apiV2.put(`${RBAC}/users/${userId}/scopes`, { grants });
	return unwrap<ScopeGrant[]>(response.data) ?? [];
};

// C14: body is exactly {flag, enabled} — no role_id (contract drift guard). Per-USER semantics.
export const toggleUserFlag = async (
	userId: number,
	payload: FlagTogglePayload
): Promise<UserAssignment> => {
	const response = await apiV2.patch(`${RBAC}/users/${userId}/flags`, payload);
	return unwrap<UserAssignment>(response.data);
};
